import { useState } from "react";
import { Bot, Sparkles, Cpu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useProjects } from "@/hooks/useProjects";
import type { LiveSessionProvider } from "@/lib/types";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (provider: LiveSessionProvider, projectId?: string) => void;
  isCreating: boolean;
};

const providers: {
  value: LiveSessionProvider;
  label: string;
  icon: typeof Bot;
  description: string;
}[] = [
  {
    value: "claude-code",
    label: "Claude Code",
    icon: Bot,
    description: "Anthropic Claude",
  },
  {
    value: "codex",
    label: "Codex",
    icon: Sparkles,
    description: "OpenAI Codex",
  },
  {
    value: "gemini",
    label: "Gemini",
    icon: Cpu,
    description: "Google Gemini",
  },
];

export function NewSessionSheet({ open, onClose, onCreate, isCreating }: Props) {
  const [selectedProvider, setSelectedProvider] =
    useState<LiveSessionProvider>("claude-code");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const { data: projects } = useProjects();

  const handleCreate = () => {
    onCreate(
      selectedProvider,
      selectedProject || undefined
    );
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="bg-zinc-950 border-zinc-800 rounded-t-2xl max-h-[80vh]">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-zinc-100">New Session</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-4">
          {/* Provider selection */}
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-2 block">
              Provider
            </label>
            <div className="grid grid-cols-3 gap-2">
              {providers.map(({ value, label, icon: Icon, description }) => (
                <button
                  key={value}
                  onClick={() => setSelectedProvider(value)}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors",
                    selectedProvider === value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{label}</span>
                  <span className="text-[10px] text-zinc-500">{description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Project dropdown (optional) */}
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-2 block">
              Project (optional)
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full h-9 rounded-md border border-zinc-700 bg-zinc-900 px-3 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">No project</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Start button */}
          <Button
            onClick={handleCreate}
            disabled={isCreating}
            className="w-full"
          >
            {isCreating ? "Starting..." : "Start Session"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
